import AsyncButton from '@components/button/AsyncButton.component';
import LoadingCard from '@components/card/LoadingCard.component';
import PliableLoader from '@components/loaders/PliableLoader.component';
import DraftVersionORM, { PublishPlan } from '@models/draftVersion';
import { requireConfirmation, runBuild } from '@services/alert/alert.service';
import ApiService, { SingleRecordResponse } from '@services/api/api.service';
import BackgroundService from '@services/bg.service';
import { getErrorMessage } from '@services/errors.service';
import toast from '@services/toast.service';
import { clearDraftData, enterDraftMode, exitDraftMode, invalidateEverything, useAvailableBranches, useDraftVersionId, useIsInDraftMode, useProjectConfig, useUserConfig } from '@stores/data.store';
import { current } from 'immer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toggleThenaWidget } from "@stores/data.store";

import styled, {css} from 'styled-components';
import PublishPlanChanges from '@components/draft/PublishPlanChanges.component';
import { Pane, PaneContent } from '@pages/PageStructure.component';
import { AuthorizedContent } from "@frontegg/react";
import { Role } from "../../constants/roles";
import { ButtonGroup, Form, Dropdown as BSDropdown, Button, Offcanvas, OffcanvasBody, Modal } from "react-bootstrap";
import FullScreenTakeover from '@components/general/FullScreenTakeover.component';
import InfoAlert from '@components/statusIndicators/InfoAlert.component';
import Inbox from '@components/Inbox.component';




const DraftBannerStyles = styled.div<{mode: 'DRAFT' | 'PROD'}>`
background-color: var(--pliable-navy);
border-bottom: solid 1px var(--ct-border-color);
height: 50px;
padding: 0px 2.25rem;
color: white;

display: flex;
align-items: center;
flex-direction: row;

.icon-button {
    color: var(--brand-gray-2);

    &:hover {
        color: white;
        cursor: pointer;
    }
}


`

interface PrInfo {
    title: string;
    description?: string;
    git_source: string;
    external_id: string;
    external_url?: string;
}



const DraftBanner = () => {
    const {data: draftVersionId} = useDraftVersionId();
    const tenantConfig = useProjectConfig();

    const userConfig = useUserConfig();

    const [loadingPullRequest, setLoadingPullRequest] = useState(false);
    const [pullRequestInfo, setPullRequestInfo] = useState<PrInfo|null>(null);
    const [publishPlan, setPublishPlan] = useState<PublishPlan|null>(null);
    const [prMessage, setPrMessage] = useState('');
    const [planning, setPlanning] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [publishMessage, setPublishMessage] = useState('');
    const navigate = useNavigate();

    const planPublish = useCallback(async () => {
        setPlanning(true);
        try {
            const plan = await DraftVersionORM.planPublish();
            console.log(plan);
            setPublishPlan(plan);
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        } finally {
            setPlanning(false);
        }
        
        
    }, [draftVersionId])

    const doPublish = useCallback(async () => {
        setPublishing(true);
        try {
            const versionIdx = publishPlan?.next_version_idx;
            // clear out the plan once we attempt to use it
            setPublishPlan(null);
            await DraftVersionORM.publishChanges(versionIdx as string, publishMessage);
            await exitDraftMode();
            navigate('/');
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        } finally {
            setPublishing(false);
        }
    }, [publishPlan])

    const [undoing, setUndoing] = useState(false);
    const abandonChanges = useCallback(async () => {
        let message: string | JSX.Element = 'Are you sure you want to undo your changes? We will copy everything in production into your develop mode.';
        const confirmed = await requireConfirmation(message, 'Revert Changes', 'Confirm', 'Cancel');
        if (confirmed) {
            setUndoing(true);
            try {
                await DraftVersionORM.undoDraftVersionId();
                await clearDraftData();
            } catch (err) {
                toast('danger', 'Error', getErrorMessage(err));
            } finally {
                setUndoing(false);
            }
        }
    }, [draftVersionId]);

    const exitDraft = useCallback(async () => {
        await exitDraftMode();
        navigate('/');
    }, [draftVersionId]);

    const showDraftBanner = useMemo(() => {
        // if we have a draftVersionId
        // and we are not on account settings (which currently have no draft mode)
        return !window.location.pathname.startsWith('/account/') && !window.location.pathname.startsWith('/onboarding');
    }, [draftVersionId]);


    const [sourceTruncate, setSourceTruncate] = useState(0);
    useEffect(() => {
        setSourceTruncate(ApiService.getInstance().sourceTruncate || 0);
    });

    const changeSourceTruncate = useCallback((newVal: number) => {
        setSourceTruncate(newVal);
        ApiService.getInstance().setSourceTruncate(newVal);
    }, []);

    const inDraftMode = useIsInDraftMode();

    const viewChanges = useCallback(() => {

    }, []);

    const checkForLatestData = useCallback(async () => {
        await runBuild('', false, true);
    }, []);

    return (
        <DraftBannerStyles mode={inDraftMode ? 'DRAFT' : 'PROD'}>
            {(planning || publishPlan) && <FullScreenTakeover noCloseButton show onHide={() => setPublishPlan(null)}>
                <div className="h-100 d-flex flex-column">
                    <div className="p-4">
                        <div className="d-flex center-vertically">
                            <h1 className="flex-1" style={{fontSize: '2.5rem'}}>{planning && 'Planning'} {!planning && publishPlan && `Publish Version #${publishPlan.next_version_idx}`}</h1>
                            <div>
                                {!planning && <div>
                                    <Button variant="secondary" className="me-1" onClick={() => setPublishPlan(null)}>
                                        Cancel
                                    </Button>
                                    <Button 
                                        variant="success"
                                        disabled={!publishPlan || publishPlan.has_any_changes == false}
                                        onClick={doPublish}
                                    >Confirm Publish</Button>
                                </div>}
                            </div>
                        </div>
                        {publishPlan && publishPlan.has_any_changes && <>
                        <h3 className="mb-3">This will publish everything you have in develop mode into production, which may affect reports or other nodes your stakeholders depend on. Make sure to review all of the changes below before publishing.</h3>
                        <div className='row'>
                            <Form.Group className="mb-3">
                                <Form.Label>Message to include with publish:</Form.Label>
                                <Form.Control as="textarea" onChange={(e) => setPublishMessage(e.target.value)} autoFocus value={publishMessage} placeholder='Write a message about your changes...' />
                            </Form.Group>
                        </div></>}
                        
                    </div>
                    <div className="flex-1">
                        <div className="h-100">
                            <Pane>
                                <PaneContent>
                                    <div className="p-4">
                                        {planning && <LoadingCard
                                            action="Calculating changes"
                                            subTitle="This may take a while if you have a lot of nodes..."
                                            minHeight="30px"
                                        /> }
                                        {publishPlan && <PublishPlanChanges plan={publishPlan} /> }
                                    </div>
                                    
                                </PaneContent>
                            </Pane>
                        </div>
                    
             
                    </div>
                </div>
                
              
                
                
             
          </FullScreenTakeover>}
          
          {publishing && <Modal show backdrop="static">
              <Modal.Header>
                  <Modal.Title>Publishing changes to production</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                  <LoadingCard action='Publishing' minHeight="30px" />
              </Modal.Body>
          </Modal>}
          {undoing && <Modal show backdrop="static">
              <Modal.Header>
                  <Modal.Title>Undoing changes</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                  <LoadingCard action='Undoing changes' minHeight="30px" />
              </Modal.Body>
          </Modal>}
            <div className="font-18">
                <i className="mdi mdi-wrench"></i> <strong>Develop Mode</strong>

            </div>
            <div className="ms-3 text-muted">
                <i className="mdi mdi-information"></i> Your data sources are not automatically updated in develop mode. <a role="button" className="force-link" onClick={checkForLatestData}>Click here</a> if you need the latest data.
            </div>
            {/* <div>
                <span className="me-1 ms-1">&bull;</span>
                <span>Last published 1 day ago</span>
            </div> */}
            <div className="flex-1">
                
            </div>
            <Inbox/>
            <button className="btn btn-sm btn-light me-1" onClick={abandonChanges}>
                <i className="mdi mdi-undo"></i> Revert
            </button>
            {/* <button className="btn btn-outline-secondary me-1">
                <i className="mdi mdi-eye"></i> Changes
            </button> */}
            <button className="btn btn-sm btn-success" onClick={planPublish}>
                <i className="mdi mdi-check-bold"></i> Publish
            </button>

          
          {/* <div className={`draft-banner-wrapper ${(!showDraftBanner) ? 'closed': ''}`}> */}
                  
                      
                  {/* <div className="draft-banner-end">
                      <button onClick={toggleThenaWidget} className="btn btn-primary start-thena-chat btn-rounded">Get Help</button>
                  </div> */}
          {/* </div> */}
      </DraftBannerStyles>
  );
}

export default DraftBanner;

